import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { forumAPI } from '../lib/api';
import { Search, MessageSquare, Eye, Clock, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

export const ForumsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [topic, setTopic] = useState(searchParams.get('topic') || 'all');

  const topics = [
    { value: 'all', label: 'All Topics' },
    { value: 'general', label: 'General Discussion' },
    { value: 'jobs', label: 'Jobs & Careers' },
    { value: 'events', label: 'Events' },
    { value: 'startups', label: 'Startups' },
    { value: 'technical', label: 'Technical' },
  ];

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (search) params.search = search;
        if (topic && topic !== 'all') params.topic = topic;
        const res = await forumAPI.getAll(params);
        setPosts(res.data);
      } catch (err) {
        console.error('Error fetching posts:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [search, topic]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (topic && topic !== 'all') params.set('topic', topic);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearch('');
    setTopic('all');
    setSearchParams({});
  };

  const hasFilters = search || (topic && topic !== 'all');

  return (
    <div data-testid="forums-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Community</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Discussion Forums</h1>
          <p className="text-muted-foreground max-w-2xl">
            Connect with Portland's tech community. Ask questions, share insights, 
            and engage in discussions about technology, careers, and local events.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border sticky top-16 z-40 bg-background">
        <div className="container-wide py-4">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search discussions..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="forums-search"
              />
            </div>
            <Select value={topic} onValueChange={setTopic}>
              <SelectTrigger className="w-[180px]" data-testid="topic-select">
                <SelectValue placeholder="Topic" />
              </SelectTrigger>
              <SelectContent>
                {topics.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" data-testid="search-btn">Search</Button>
            {hasFilters && (
              <Button type="button" variant="ghost" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </form>
        </div>
      </section>

      {/* Posts */}
      <section>
        <div className="container-wide py-12">
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="border border-border p-6 animate-pulse">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                  <div className="h-3 bg-muted rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : posts.length > 0 ? (
            <div className="space-y-4">
              {posts.map((post, index) => (
                <Link
                  key={post.id}
                  to={`/forums/${post.id}`}
                  className="block animate-slide-up"
                  style={{ animationDelay: `${index * 50}ms` }}
                  data-testid={`forum-post-${post.id}`}
                >
                  <Card className="card-hover">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="badge-category">{post.topic}</span>
                          </div>
                          <h3 className="font-semibold text-lg mb-2">{post.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {post.content}
                          </p>
                          <div className="flex items-center gap-4 mt-4 text-xs text-muted-foreground">
                            <span>by {post.author_name}</span>
                            {post.created_at && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(post.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            {post.reply_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {post.view_count || 0}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <MessageSquare className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg mb-2">No discussions yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Be the first to start a conversation
              </p>
              <Button variant="outline">Start a Discussion</Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
